import { get } from '../components/library/api/rest/index';
import { getStaticBaseUrl } from '../utils/publicUrl';
import { unwrapXmlStyleContent } from '../utils/apiFieldText';
import { isInactiveSystemStatusRow } from '../utils/systemStatus';
import { buildProcessFlowViewDetailsUrl } from '../utils/processFlowViewerUrl';
import { fetchDepartmentRowForPermitHero, resolveSidebarAssetUrl } from './landingDepartmentsGrid';
import { permitsPathSegment } from './landingDepartmentMerge';
/**
 * Agencies + permits for department permit list page.
 * Replace URL templates when backend is ready; until then uses `/mock-api/...` JSON.
 */
function resolveFetchUrl(path) {
    if (path.startsWith('http'))
        return path;
    const base = getStaticBaseUrl().replace(/\/?$/, '/');
    const rel = path.startsWith('/') ? path.slice(1) : path;
    return `${base}${rel}`;
}
function resolveHeroAssetUrl(sidebarBase, raw) {
    const t = raw?.trim();
    if (!t)
        return undefined;
    if (/^https?:\/\//i.test(t))
        return t;
    if (t.startsWith('/'))
        return t;
    if (sidebarBase?.trim()) {
        const u = resolveSidebarAssetUrl(sidebarBase.trim(), t);
        return u || undefined;
    }
    return t;
}
function landingDepartmentItemToHeroDto(item, sidebarBase) {
    if (!item?.title?.trim())
        return undefined;
    return {
        title: item.title.trim(),
        subtitle: item.subtitle?.trim() || undefined,
        icon: item.icon,
        iconAccentColor: item.accentColor,
        logoImage: resolveHeroAssetUrl(sidebarBase, item.logoImage),
        backgroundImage: resolveHeroAssetUrl(sidebarBase, item.image),
    };
}
/**
 * Derives the department hero from landing grid rows already in Redux (`landingGrids.departmentsRaw`)
 * instead of re-querying the departments master grid — avoids a duplicate request on the permits page.
 * Matches `:departmentId` against each row's `recordId` (exact) or its `/permits/:id` href segment.
 */
export function findLandingDepartmentHero(departmentId, departments, sidebarBase) {
    const id = departmentId.trim().toLowerCase();
    if (!id || !departments.length)
        return undefined;
    let match;
    for (const d of departments) {
        const rid = (d.recordId ?? '').trim().toLowerCase();
        if (rid && rid === id) {
            match = d;
            break;
        }
        const seg = permitsPathSegment(d.href);
        if (seg && seg === id) {
            match = d;
            break;
        }
    }
    return match ? landingDepartmentItemToHeroDto(match, sidebarBase) : undefined;
}
/** Maps API payloads that use `NAME`, `ICON` (and camelCase) onto {@link DepartmentHeroDto}. */
export function normalizeDepartmentHero(raw) {
    if (raw === null || raw === undefined || typeof raw !== 'object')
        return undefined;
    const r = raw;
    const trimStr = (v) => {
        if (typeof v !== 'string')
            return undefined;
        const t = unwrapXmlStyleContent(v);
        return t.length > 0 ? t : undefined;
    };
    const title = trimStr(r.title) ??
        trimStr(r.NAME) ??
        trimStr(r.Name) ??
        trimStr(r.name) ??
        '';
    const t = title.trim();
    if (!t)
        return undefined;
    return {
        title: t,
        subtitle: trimStr(r.subtitle) ?? trimStr(r.SUBTITLE) ?? undefined,
        icon: trimStr(r.icon) ?? trimStr(r.ICON) ?? undefined,
        logoImage: trimStr(r.logoImage) ?? trimStr(r.LOGO_IMAGE) ?? undefined,
        iconAccentColor: trimStr(r.iconAccentColor) ?? trimStr(r.ICON_ACCENT_COLOR) ?? undefined,
        backgroundImage: trimStr(r.backgroundImage) ??
            trimStr(r.BACKGROUND_IMAGE) ??
            trimStr(r.heroImage) ??
            trimStr(r.HERO_IMAGE) ??
            undefined,
    };
}
/**
 * `contains` on a single digit matches wrong rows (e.g. "4" matches "14"). Use `eq` for numeric ids.
 */
function departmentFieldFilter(departmentId) {
    const safe = departmentId.replace(/'/g, "''");
    return /^\d+$/.test(departmentId.trim()) ? `DEPARTMENT~eq~'${safe}'` : `DEPARTMENT~contains~'${safe}'`;
}
function agencyFieldFilter(agencyId) {
    const safe = agencyId.replace(/'/g, "''");
    return /^\d+$/.test(agencyId.trim()) ? `AGENCY~eq~'${safe}'` : `AGENCY~contains~'${safe}'`;
}
function rankAgencyTitleForPriority(title) {
    const t = title.trim().toLowerCase();
    /* `t` is lowercased, so compare against lowercase — Business Registration sorts first. */
    if (t.includes('business registration'))
        return 0;
    return 1;
}
function compareAgencyTitlesWithPriority(a, b) {
    const ar = rankAgencyTitleForPriority(a.title);
    const br = rankAgencyTitleForPriority(b.title);
    if (ar !== br)
        return ar - br;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
}
/**
 * Builds `{Sidebar}/api/PublicFlowBuilderRecord/Grid/{gridId}?filter=…&page=&pageSize=`.
 * Numeric `:departmentId` uses `DEPARTMENT~eq~` so `/permits/4` does not match department `14`.
 */
export function buildDepartmentAgenciesGridUrl(sidebarBase, gridId, departmentId, options) {
    const root = sidebarBase.replace(/\/$/, '');
    const page = options?.page ?? 1;
    const pageSize = options?.pageSize ?? 25;
    const filter = departmentFieldFilter(departmentId);
    const params = new URLSearchParams();
    params.set('filter', filter);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return `${root}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(gridId)}?${params.toString()}`;
}
function gridRows(json) {
    const d = json.data ?? json.Data;
    return Array.isArray(d) ? d : [];
}
/**
 * Loads agencies for a department from the sidebar Flow Builder grid (filter by `DEPARTMENT` field).
 * When `options.departmentsMasterGridId` is set (same as `LandingDepartmentsGridId` in env), also requests the
 * departments master grid so the hero gets a `department` title (agencies grid rows do not include department metadata).
 */
export function fetchAgenciesFromSidebarDepartmentGrid(sidebarBase, gridId, departmentId, pageSize = 100, options) {
    const url = buildDepartmentAgenciesGridUrl(sidebarBase, gridId, departmentId, { page: 1, pageSize });
    return get(url, null, null, null).then(async (json) => {
        if (json === null) {
            throw new Error('Failed to load agencies grid');
        }
        const typed = json;
        const rows = gridRows(typed);
        const agencies = [];
        for (const row of rows) {
            const recordId = row.RecordId ?? row.recordId;
            const id = recordId != null && String(recordId).trim() !== ''
                ? String(recordId)
                : row.Id != null
                    ? String(row.Id)
                    : '';
            if (!id)
                continue;
            const titleRaw = row.NAME ?? row.Name ?? row.name;
            const title = typeof titleRaw === 'string' && titleRaw.trim()
                ? unwrapXmlStyleContent(titleRaw).trim()
                : `Agency ${id}`;
            agencies.push({ id, title });
        }
        agencies.sort(compareAgencyTitlesWithPriority);
        let department;
        const masterId = options?.departmentsMasterGridId?.trim();
        if (masterId) {
            try {
                const item = await fetchDepartmentRowForPermitHero(sidebarBase, masterId, departmentId);
                department = landingDepartmentItemToHeroDto(item, sidebarBase);
            }
            catch {
                /* non-fatal — fall back to page JSON or numeric title */
            }
        }
        return {
            agencies,
            department,
            defaultDepartmentId: undefined,
        };
    });
}
export function resolveAgenciesUrl(departmentId, template) {
    const id = encodeURIComponent(departmentId);
    if (template && template.trim().length > 0) {
        return template.replace(/\{departmentId\}/g, id);
    }
    return `/mock-api/departments/${id}/agencies.json`;
}
export function resolvePermitsUrl(agencyId, template) {
    const id = encodeURIComponent(agencyId);
    if (template && template.trim().length > 0) {
        return template.replace(/\{agencyId\}/g, id);
    }
    return `/mock-api/agencies/${id}/permits.json`;
}
export async function fetchDepartmentAgenciesPage(departmentId, urlTemplate) {
    const url = resolveFetchUrl(resolveAgenciesUrl(departmentId, urlTemplate));
    const data = (await get(url, null, null, null));
    if (!data)
        throw new Error('Failed to load department');
    return {
        agencies: Array.isArray(data.agencies) ? data.agencies : [],
        department: normalizeDepartmentHero(data.department),
        defaultDepartmentId: data.defaultDepartmentId,
    };
}
/**
 * This endpoint returns `PermitListItemDto`-shaped JSON directly (no grid-row remapping), but
 * `APPLY_NOW` was added as a raw column name inconsistent with the DTO's camelCase `applyNow` —
 * normalize it here so the field is picked up regardless of which casing the API sends.
 */
function normalizePermitApplyNow(raw) {
    const r = raw;
    const v = r?.applyNow ?? r?.APPLY_NOW ?? r?.apply_now ?? r?.ApplyNow;
    return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}
export async function fetchPermitsForAgency(agencyId, urlTemplate) {
    const url = resolveFetchUrl(resolvePermitsUrl(agencyId, urlTemplate));
    const json = (await get(url, null, null, null));
    if (!json)
        throw new Error('Failed to load permits');
    if (!Array.isArray(json.permits))
        return [];
    return json.permits
        .filter((p) => !isInactiveSystemStatusRow(p))
        .map((p) => ({ ...p, applyNow: normalizePermitApplyNow(p) }));
}
export { buildProcessFlowViewDetailsUrl };
/** Resolves `FlowVersion` from grid row payloads (Pascal/camel/underscore; string or number). */
export function readPermitGridFlowVersion(row) {
    const r = row;
    const keys = ['FlowVersion', 'flowVersion', 'FLOW_VERSION', 'Flow_Version', 'flowversion'];
    for (const k of keys) {
        const v = r[k];
        if (typeof v === 'string' && v.trim())
            return v.trim();
        if (typeof v === 'number' && Number.isFinite(v))
            return String(v);
    }
    return undefined;
}
/**
 * Combined filter for permit rows; numeric ids use `eq` so agency `4` does not match `14`.
 */
export function buildAgencyPermitsGridUrl(sidebarBase, gridId, departmentId, agencyId, options) {
    const root = sidebarBase.replace(/\/$/, '');
    const page = options?.page ?? 1;
    const pageSize = options?.pageSize ?? 25;
    const filter = `(${agencyFieldFilter(agencyId)}~and~${departmentFieldFilter(departmentId)})`;
    const params = new URLSearchParams();
    params.set('filter', filter);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return `${root}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(gridId)}?${params.toString()}`;
}
export function mapPermitGridRowToDto(row, departmentId, options) {
    const titleRaw = row.TITLE ?? row.title;
    const title = typeof titleRaw === 'string' && titleRaw.trim()
        ? unwrapXmlStyleContent(titleRaw).trim()
        : 'Permit';
    const descRaw = row.DESCRIPTION ?? row.description;
    const description = typeof descRaw === 'string' && descRaw.trim() ? unwrapXmlStyleContent(descRaw).trim() : undefined;
    const costRaw = row.APPLICATION_COST ?? row.application_cost;
    let priceRange;
    if (costRaw != null && String(costRaw).trim() !== '') {
        const s = String(costRaw).trim();
        priceRange = s.startsWith('$') ? s : `$${s}`;
    }
    const timeRaw = row.PROCESSING_TIME ?? row.processing_time;
    const timeframe = typeof timeRaw === 'string' && timeRaw.trim() ? unwrapXmlStyleContent(timeRaw).trim() : undefined;
    const scopeRaw = row.SCOPE ?? row.scope;
    const scope = typeof scopeRaw === 'string' && scopeRaw.trim() ? unwrapXmlStyleContent(scopeRaw).trim() : undefined;
    const applyNowRaw = row.APPLY_NOW ?? row.apply_now ?? row.applyNow;
    const applyNow = typeof applyNowRaw === 'string' && applyNowRaw.trim() ? applyNowRaw.trim() : undefined;
    const rec = row.RecordId ?? row.recordId;
    const recordId = rec != null && String(rec).trim() !== '' ? String(rec) : undefined;
    const flowIdRaw = row.FlowId ?? row.flowId ?? options?.fallbackFlowId;
    const flowId = typeof flowIdRaw === 'string' && flowIdRaw.trim() ? flowIdRaw.trim() : undefined;
    /** Prefer explicit grid `FlowVersion` (and aliases); only then default for legacy rows without the column. */
    const flowVersion = readPermitGridFlowVersion(row) ?? '1.0';
    const template = options?.processFlowDetailsUrl?.trim() ?? '';
    /** Raw Flow Viewer URL — `DepartmentPermitsPage` wraps this into `/permits/:id/details?viewDetails=…` for the iframe. */
    let detailsHref;
    if (template && flowId && recordId) {
        const flowUrl = buildProcessFlowViewDetailsUrl(template, flowId, flowVersion, recordId);
        detailsHref = flowUrl || undefined;
    }
    return {
        title,
        description,
        timeframe,
        priceRange,
        scope,
        applyNow,
        recordId,
        detailsHref,
    };
}
/**
 * All permit rows for one agency — paginates until `total` is reached (same approach as
 * `fetchLandingPermitsCatalogFromGrid`). A single-page fetch here previously capped every
 * agency's permit list (and its client-side search) at `pageSize` (default 25), so an agency
 * with 300 permits only ever showed/searched the first page.
 */
export async function fetchPermitsFromSidebarAgencyGrid(sidebarBase, gridId, departmentId, agencyId, pageSize = 25, options) {
    const mapOpts = {
        processFlowDetailsUrl: options?.processFlowDetailsUrl,
        fallbackFlowId: gridId,
    };
    const size = Math.min(500, Math.max(1, Math.floor(pageSize)));
    const all = [];
    let page = 1;
    let fetchedRaw = 0;
    let total = Infinity;
    while (fetchedRaw < total) {
        const url = buildAgencyPermitsGridUrl(sidebarBase, gridId, departmentId, agencyId, { page, pageSize: size });
        const json = (await get(url, null, null, null));
        if (!json)
            throw new Error('Failed to load permits');
        const rows = gridRows(json);
        total = json.total ?? json.Total ?? rows.length;
        fetchedRaw += rows.length;
        /** Inactive rows (`SYSTEM_STATUS = 'I'`) are dropped before mapping so they never render. */
        for (const row of rows) {
            if (isInactiveSystemStatusRow(row))
                continue;
            all.push(mapPermitGridRowToDto(row, departmentId, mapOpts));
        }
        /** A partial (or empty) page is the last page — avoids an extra request past the true end. */
        if (rows.length < size)
            break;
        page += 1;
        if (page > 500)
            break;
    }
    return all;
}
